import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import {CheckCircle2} from 'lucide-react';
import { fontWeights } from "../../styles/typography";
import { trackPopupInteraction } from "../../utils/trackingHelpers";

interface Notification {
  id: number;
  name: string;
  fullName: string;
  action: string;
  initials: string;
  photo: string;
  facebookProfile: string;
  archetype: 'caotico' | 'rehen' | 'soberano';
}

const notifications: Notification[] = [
  { 
    id: 1, 
    name: "Sebastián", 
    fullName: "Sebastián Delgado", 
    action: "Automatizó todo su follow-up y cerró 8 ventas en 15 días", 
    initials: "SD",
    photo: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&h=100&fit=crop&q=80",
    facebookProfile: "https://facebook.com/sebastian.delgado.mx",
    archetype: 'caotico'
  },
  { 
    id: 2, 
    name: "Lucía", 
    fullName: "Lucía Ortiz", 
    action: "Dejó HubSpot y ahorra $1,240 USD/mes sin perder funciones", 
    initials: "LO",
    photo: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop&q=80",
    facebookProfile: "https://facebook.com/lucia.ortiz.es",
    archetype: 'rehen'
  },
  { 
    id: 3, 
    name: "Javier", 
    fullName: "Javier Herrera", 
    action: "Eliminó Kajabi y recupera 100% de sus ventas sin comisiones", 
    initials: "JH",
    photo: "https://images.unsplash.com/photo-1545167622-3a6ac756afa4?w=100&h=100&fit=crop&q=80",
    facebookProfile: "https://facebook.com/javier.herrera.co",
    archetype: 'soberano'
  },
  { 
    id: 4, 
    name: "Daniela", 
    fullName: "Daniela Rivas", 
    action: "Pasó de perder 70% leads a convertir 42% en 30 días", 
    initials: "DR",
    photo: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&h=100&fit=crop&q=80",
    facebookProfile: "https://facebook.com/daniela.rivas.ar",
    archetype: 'caotico'
  },
  { 
    id: 5, 
    name: "Ricardo", 
    fullName: "Ricardo Campos", 
    action: "Migró de Zoho y ahora crece sin pagar más por contacto", 
    initials: "RC",
    photo: "https://images.unsplash.com/photo-1463453091185-61582044d556?w=100&h=100&fit=crop&q=80",
    facebookProfile: "https://facebook.com/ricardo.campos.cl",
    archetype: 'rehen'
  },
  { 
    id: 6, 
    name: "Isabel", 
    fullName: "Isabel Domínguez", 
    action: "Cobra al instante sus cursos sin retenciones de 15 días", 
    initials: "ID",
    photo: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=100&h=100&fit=crop&q=80",
    facebookProfile: "https://facebook.com/isabel.dominguez.pe",
    archetype: 'soberano'
  },
  { 
    id: 7, 
    name: "Carlos", 
    fullName: "Carlos Paredes", 
    action: "Unificó 6 herramientas en una y ahorra $520 USD/mes", 
    initials: "CP",
    photo: "https://images.unsplash.com/photo-1552058544-f2b08422138a?w=100&h=100&fit=crop&q=80",
    facebookProfile: "https://facebook.com/carlos.paredes.mx",
    archetype: 'caotico'
  },
  { 
    id: 8, 
    name: "Gabriela", 
    fullName: "Gabriela Méndez", 
    action: "Canceló Salesforce y triplicó velocidad de respuesta", 
    initials: "GM",
    photo: "https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?w=100&h=100&fit=crop&q=80",
    facebookProfile: "https://facebook.com/gabriela.mendez.es",
    archetype: 'rehen'
  }
];

export const SocialProof = () => {
  const [currentNotification, setCurrentNotification] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hasTracked, setHasTracked] = useState(false);

  useEffect(() => {
    // Mostrar después de 8 segundos (más sutil)
    const initialDelay = setTimeout(() => {
      setIsVisible(true);
      if (!hasTracked) {
        trackPopupInteraction('social_proof', 'viewed');
        setHasTracked(true);
      }
    }, 8000);

    // Rotar cada 12 segundos (más tiempo visible)
    const interval = setInterval(() => {
      setIsVisible(false);
      setTimeout(() => {
        setCurrentNotification(prev => (prev + 1) % notifications.length);
        setIsVisible(true);
      }, 600);
    }, 12000);

    return () => {
      clearTimeout(initialDelay);
      clearInterval(interval);
    };
  }, [hasTracked]);

  const notification = notifications[currentNotification];

  return (
    <AnimatePresence mode="wait">
      {isVisible && (
        <motion.div
          key={notification.id}
          initial={{ x: -100, opacity: 0, scale: 0.95 }}
          animate={{ x: 0, opacity: 1, scale: 1 }}
          exit={{ x: -100, opacity: 0, scale: 0.95 }}
          transition={{ 
            type: "spring", 
            stiffness: 260, 
            damping: 20,
            opacity: { duration: 0.3 }
          }}
          className="fixed bottom-6 left-4 z-50 max-w-[340px] pointer-events-auto"
        >
          <div className="bg-gradient-to-br from-gray-800/95 to-gray-900/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/10 overflow-hidden">
            {/* Main Content */}
            <div className="p-4 flex items-center gap-3">
              {/* Avatar con foto real */}
              <a 
                href={notification.facebookProfile} 
                target="_blank" 
                rel="noopener noreferrer"
                className="relative flex-shrink-0 group"
                onClick={(e) => {
                  e.stopPropagation();
                  trackPopupInteraction('social_proof', 'facebook_clicked');
                }}
              >
                <img 
                  src={notification.photo} 
                  alt={notification.fullName}
                  className="w-12 h-12 rounded-full object-cover shadow-lg ring-2 ring-white/20 group-hover:ring-purple-400 transition-all"
                  loading="lazy"
                />
                {/* Check verde de verificación */}
                <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-0.5 shadow-lg">
                  <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                </div>
              </a>

              {/* Texto */}
              <div className="flex-1 min-w-0">
                <a 
                  href={notification.facebookProfile} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="group"
                  onClick={(e) => {
                    e.stopPropagation();
                    trackPopupInteraction('social_proof', 'name_clicked');
                  }}
                >
                  <p className={`text-white text-sm ${fontWeights.semibold} leading-tight mb-1 group-hover:text-purple-400 transition-colors`}>
                    {notification.fullName}
                  </p>
                </a>
                <p className="text-gray-300 text-xs leading-tight">
                  {notification.action}
                </p>
                <p className="text-gray-500 text-[10px] mt-1">
                  Hace {Math.floor(Math.random() * 15) + 5} minutos
                </p>
              </div>

              {/* Botón cerrar sutil */}
              <button
                onClick={() => {
                  trackPopupInteraction('social_proof', 'closed');
                  setIsVisible(false);
                }}
                className="flex-shrink-0 text-gray-400 hover:text-white transition-colors p-1 hover:bg-white/10 rounded-lg"
                aria-label="Cerrar"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};